import { prepareInput } from 'app/src/elements/hookForms';

export const visibleAccounts = ({ accounts, txnLines }) => {
  return accounts.filter(account => {
    if (!account.hiddenAt) { return true; }

    const lineWithAccount = txnLines.find(txnLine => (
      txnLine.account?.id === account.value
    ));

    return !!lineWithAccount;
  });
};

export const specialAccount = ({ accounts, special }) => {
  return accounts.find(account => account.special === special);
};

export const getBalance = ({ accounts, txnLines }) => {
  const balance = txnLines.reduce((total, line) => {
    if (line.debitLessCredit && line.accountId) {
      const account = accounts.find(entry => (
        entry.value === line.accountId
      ));

      if (account.posting) {
        return total + parseFloat(line.debitLessCredit);
      } else {
        return total;
      }
    } else {
      return total;
    }
  }, 0.0);

  return balance;
};

const getBuyerFees = saleAsset => (
  saleAsset.asset.fees.filter(fee => (
    fee.buyerFee && (!fee.saleAssetId || fee.saleAssetId === saleAsset.id)
  ))
);

const getSellerFees = saleAsset => (
  saleAsset.asset.fees.filter(fee => !fee.buyerFee)
);

export const creditOrDebitTxnLine = txnLine => {
  if (txnLine.debitLessCredit >= 0) {
    const { creditLessDebit, ...retval } = txnLine;
    return retval;
  } else {
    const { debitLessCredit, ...retval } = txnLine;
    return { ...retval, creditLessDebit: -debitLessCredit };
  }
};

export const creditLessDebitTxnLine = txnLine => {
  const { debitLessCredit, ...retval } = txnLine;
  return { ...retval, creditLessDebit: -debitLessCredit };
};

export const debitLessCreditTxnLine = txnLine => {
  if (txnLine.creditLessDebit) {
    const { creditLessDebit, ...retval } = txnLine;
    const debitLessCredit = creditLessDebit ? -creditLessDebit : creditLessDebit;
    console.log('lines', txnLine, debitLessCredit);
    return { ...retval, debitLessCredit };
  } else {
    const { creditLessDebit, ...remainingAttributes } = txnLine;
    return remainingAttributes;
  }
};

export const prepareTxnInput = (input, fields, options) => {
  const {
    invoiceAmounts,
    creditMemoAmounts,
    saleAssetAmounts,
    feeAmounts,
    accounts,
    originalLines,
    sellerInvoice,
  } = (options || {});

  const variables = prepareInput(input, fields);

  if (invoiceAmounts) {
    const invoiceLines = invoiceAmounts.map(invoiceAmount => ({
      accountId: specialAccount({ accounts, special: 'ar' }).id,
      debitLessCredit: -invoiceAmount.amount,
      offsetId: invoiceAmount.id,
    }));

    variables.txnLinesAttributes = [
      ...variables.txnLinesAttributes,
      ...invoiceLines,
    ];

    originalLines?.forEach(originalLine => {
      if (originalLine.offsetId && 'ar' === originalLine.account.special) {
        const matchLine = variables.txnLinesAttributes
          .find(line => line.offsetId === originalLine.offsetId);

        if (matchLine) {
          matchLine.id = originalLine.id;
        } else {
          variables.txnLinesAttributes.push({
            id: originalLine.id,
            debitLessCredit: 0,
            accountId: originalLine.account.id,
            _destroy: true
          });
        }
      }
    });
  }

  if (creditMemoAmounts) {
    const creditMemoLines = creditMemoAmounts.map(creditMemoAmount => ({
      accountId: specialAccount({ accounts, special: 'ar' }).id,
      debitLessCredit: -creditMemoAmount.amount,
      offsetId: creditMemoAmount.id,
    }));

    variables.txnLinesAttributes = [
      ...variables.txnLinesAttributes,
      ...creditMemoLines,
    ];

    originalLines?.forEach(originalLine => {
      if (originalLine.offsetId && 'ar' === originalLine.account.special) {
        const matchLine = variables.txnLinesAttributes
          .find(line => line.offsetId === originalLine.offsetId);

        if (matchLine) {
          matchLine.id = originalLine.id;
        } else {
          variables.txnLinesAttributes.push({
            id: originalLine.id,
            debitLessCredit: 0,
            accountId: originalLine.account.id,
            _destroy: true
          });
        }
      }
    });
  }

  if (saleAssetAmounts) {
    const saleAssetsLines = [];

    if (sellerInvoice) {
      saleAssetAmounts.forEach(saleAsset => {
        saleAssetsLines.push({
          accountId: saleAsset.assetSellerAccount.id,
          assetId: saleAsset.asset.id,
          saleAssetId: saleAsset.id,
          debitLessCredit: (saleAsset.sellerPrice || saleAsset.soldOrIfPrice),
        });

        getSellerFees(saleAsset).forEach(fee => {
          saleAssetsLines.push({
            accountId: fee.invoiceOffsetAccountId,
            offsetId: fee.invoiceOffsetLineId,
            assetId: saleAsset.asset.id,
            saleAssetId: saleAsset.id,
            feeId: fee.id,
            debitLessCredit: -fee.amount,
          });
        });
      });
    } else {
      saleAssetAmounts.forEach(saleAsset => {
        saleAssetsLines.push({
          accountId: specialAccount({ accounts, special: 'sales' }).id,
          assetId: saleAsset.asset.id,
          saleAssetId: saleAsset.id,
          debitLessCredit: -saleAsset.soldOrIfPrice,
        });

        getBuyerFees(saleAsset).forEach(fee => {
          saleAssetsLines.push({
            accountId: fee.invoiceOffsetAccountId,
            offsetId: fee.invoiceOffsetLineId,
            assetId: saleAsset.asset.id,
            saleAssetId: saleAsset.id,
            feeId: fee.id,
            debitLessCredit: -fee.amount,
          });
        });
      });
    }

    variables.txnLinesAttributes = [
      ...variables.txnLinesAttributes,
      ...saleAssetsLines,
    ];
  }

  variables.txnLinesAttributes =
    variables.txnLinesAttributes.map(debitLessCreditTxnLine).filter(txnLine => (
      (txnLine.debitLessCredit && txnLine.accountId) || txnLine.id
    ))

  variables.txnLinesAttributes.forEach(txnLine => {
    if (!txnLine.debitLessCredit || !txnLine.accountId) {
      txnLine.debitLessCredit = '0';
      txnLine._destroy = true;
    }
  });

  if (feeAmounts) {
    const feeLines = feeAmounts.map(feeAmount => ({
      accountId: feeAmount.invoiceOffsetAccountId,
      offsetId: feeAmount.invoiceOffsetLineId,
      debitLessCredit: -feeAmount.amount,
      assetId: feeAmount.assetId,
      saleAssetId: feeAmount.saleAssetId,
      feeId: feeAmount.id,
    }));

    variables.txnLinesAttributes = [
      ...variables.txnLinesAttributes,
      ...feeLines,
    ];
  }

  return variables;
};
